import { SUPPLIER_BACKEND_API_ENDPOINTS } from "../config/apiConfig";
import { apiClient } from "./api-client.service";
import type { ApiResponse } from "./api-client.service";

import type {
  Notification,
  NotificationFilter,
  NotificationsResponse,
  NotificationPayload,
} from "../types/notifications.types";

const endpoints = SUPPLIER_BACKEND_API_ENDPOINTS.notifications;

const failure = (error: unknown, fallback: string) => ({
  success: false,
  statusCode: 500,
  message: error instanceof Error ? error.message : fallback,
  resData: null,
});

export const notificationsService = {
  async list(filter: NotificationFilter): Promise<NotificationsResponse> {
    try {
      const params = new URLSearchParams();
      Object.entries(filter).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== "") {
          params.set(key, String(value));
        }
      });

      return await apiClient<NotificationsResponse>(endpoints.list(params.toString()), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load notifications.") as unknown as NotificationsResponse;
    }
  },

  async details(id: number | string): Promise<ApiResponse<Notification>> {
    try {
      return await apiClient<ApiResponse<Notification>>(endpoints.details(id), {
        method: "GET",
        isAuthenticated: true,
        cache: "no-store",
      });
    } catch (error) {
      return failure(error, "Failed to load notification details.") as unknown as ApiResponse<Notification>;
    }
  },

  async create(payload: NotificationPayload): Promise<ApiResponse<Notification>> {
    try {
      return await apiClient<ApiResponse<Notification>>(endpoints.create, {
        method: "POST",
        body: JSON.stringify(payload),
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to create notification.") as unknown as ApiResponse<Notification>;
    }
  },

  async markRead(id: number | string): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.singleMarkRead(id), {
        method: "PATCH",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to mark notification as read.") as unknown as ApiResponse<unknown>;
    }
  },

  async markAllRead(): Promise<ApiResponse<unknown>> {
    try {
      return await apiClient<ApiResponse<unknown>>(endpoints.markAllRead, {
        method: "PATCH",
        isAuthenticated: true,
      });
    } catch (error) {
      return failure(error, "Failed to mark all notifications as read.") as unknown as ApiResponse<unknown>;
    }
  },
};
